import { graphql } from 'react-relay';

import { isMobileUserAgent, shouldShowMobileWeb } from '@confluence/mobile-detection';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { fg } from '@confluence/feature-gating';
import { ONBOARDING_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { ContentStatus } from '@confluence/page-utils/entry-points/enums.ts';

import type {
	useIsEligibleForContextualCollabOnboardingLastViewedTimeQuery$data,
	useIsEligibleForContextualCollabOnboardingLastViewedTimeQuery$variables,
} from './__generated__/useIsEligibleForContextualCollabOnboardingLastViewedTimeQuery.graphql.ts';
import type {
	useIsEligibleForContextualCollabOnboardingContentContributorsQuery$data,
	useIsEligibleForContextualCollabOnboardingContentContributorsQuery$variables,
} from './__generated__/useIsEligibleForContextualCollabOnboardingContentContributorsQuery.graphql.ts';
import type {
	useIsEligibleForContextualCollabOnboardingGeneratedByQuery$data,
	useIsEligibleForContextualCollabOnboardingGeneratedByQuery$variables,
} from './__generated__/useIsEligibleForContextualCollabOnboardingGeneratedByQuery.graphql.ts';

type UseIsEligibleForContextualCollabOnboardingArgs = {
	contentId: string;
	contentType: string;
	contentStatus?: string;
	isLivePage: boolean;
	skip: boolean;
};

/**
 * Eligibility hook for contextual collab onboarding for new end users experiment with following requirements to be eligible:
 *
 * Feature gate enabled
 * Is Nth user
 * Desktop user
 * Not auto-generated content
 * User is not a contributor
 * Should be >1st visit
 * Should be a page and not in the editor
 */

export const useIsEligibleForContextualCollabOnboarding = ({
	contentId,
	contentType,
	contentStatus,
	isLivePage,
	skip,
}: UseIsEligibleForContextualCollabOnboardingArgs) => {
	const isEditMode = useIsEditorPage();
	const isDesktopUser = !(isMobileUserAgent() || shouldShowMobileWeb());
	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN],
		skip ||
			(!fg('cc_engagement_contextual_collab_onboarding_for_neu') &&
				!fg('cc_engagement_contextual_collab_neu_aa_gate')),
	);

	const { isFoundingAdmin } = deserializeState(onboardingStateData);

	const isNthUser = !isFoundingAdmin && !onboardingStateLoading && !onboardingStateError;

	const isValidContent = (contentType === 'page' && !isEditMode) || isLivePage;

	const generatedByQuery = useQueryInterop<
		useIsEligibleForContextualCollabOnboardingGeneratedByQuery$data,
		useIsEligibleForContextualCollabOnboardingGeneratedByQuery$variables
	>(
		graphql`
			query useIsEligibleForContextualCollabOnboardingGeneratedByQuery(
				$contentId: ID!
				$status: String!
				$includeGeneratedBy: Boolean = false
			) {
				content(id: $contentId, status: [$status]) {
					nodes {
						contentProperties @include(if: $includeGeneratedBy) {
							latest {
								generatedBy
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
				status: contentStatus ?? '',
				// Feature flag to control whether to fetch generatedBy field
				includeGeneratedBy: fg('confluence_frontend_confluence_creator_onboarding'),
			},
			skip:
				skip ||
				!isNthUser ||
				!contentId ||
				!isValidContent ||
				contentStatus === ContentStatus.DRAFT ||
				(!fg('cc_engagement_contextual_collab_onboarding_for_neu') &&
					!fg('cc_engagement_contextual_collab_neu_aa_gate')),
		},
	);

	const generatedBy =
		generatedByQuery.data?.content?.nodes?.[0]?.contentProperties?.latest?.generatedBy;
	// Copied from GeneratedBy.java Monolith file
	const isAutoGenerated =
		!generatedByQuery.error &&
		!generatedByQuery.loading &&
		(generatedBy === 'ONBOARDING' || generatedBy === 'ROVO_SMART_CREATION');

	const lastViewedTimeQuery = useQueryInterop<
		useIsEligibleForContextualCollabOnboardingLastViewedTimeQuery$data,
		useIsEligibleForContextualCollabOnboardingLastViewedTimeQuery$variables
	>(
		graphql`
			query useIsEligibleForContextualCollabOnboardingLastViewedTimeQuery(
				$contentId: ID!
				$contentType: CatchupContentType!
			) {
				catchupGetLastViewedTime(contentId: $contentId, contentType: $contentType) {
					lastViewedTimeISO
				}
			}
		`,
		{
			variables: {
				// The query is skipped if the id and type are not valid
				contentId,
				contentType: 'PAGE',
			},
			skip:
				skip ||
				!isNthUser ||
				!isValidContent ||
				isAutoGenerated ||
				(!fg('cc_engagement_contextual_collab_onboarding_for_neu') &&
					!fg('cc_engagement_contextual_collab_neu_aa_gate')),
		},
	);

	// Copied from useDynamicSplitButton.ts
	const lastViewedTime =
		lastViewedTimeQuery.data?.catchupGetLastViewedTime?.lastViewedTimeISO ?? '';
	const cleanedLastViewedTime = lastViewedTime.replace(/\[.*?\]$/, '');
	const parsedLastViewedTime = Date.parse(cleanedLastViewedTime);
	const isFirstTimeVisit =
		!lastViewedTimeQuery.error &&
		!lastViewedTimeQuery.loading &&
		Number.isNaN(parsedLastViewedTime);

	const contributorQuery = useQueryInterop<
		useIsEligibleForContextualCollabOnboardingContentContributorsQuery$data,
		useIsEligibleForContextualCollabOnboardingContentContributorsQuery$variables
	>(
		graphql`
			query useIsEligibleForContextualCollabOnboardingContentContributorsQuery($contentId: ID!) {
				contentContributors(id: $contentId) {
					isCurrentUserContributor
				}
			}
		`,
		{
			variables: { contentId },
			skip:
				skip ||
				!isNthUser ||
				!isValidContent ||
				isFirstTimeVisit ||
				isAutoGenerated ||
				(!fg('cc_engagement_contextual_collab_onboarding_for_neu') &&
					!fg('cc_engagement_contextual_collab_neu_aa_gate')),
		},
	);
	const isCurrentUserContributor =
		!contributorQuery.error &&
		!contributorQuery.loading &&
		Boolean(contributorQuery.data?.contentContributors?.isCurrentUserContributor);

	return {
		isEligibleForSpotlight:
			isDesktopUser &&
			isNthUser &&
			isValidContent &&
			!isAutoGenerated &&
			!isFirstTimeVisit &&
			!isCurrentUserContributor &&
			fg('cc_engagement_contextual_collab_onboarding_for_neu'),
	};
};
